import * as types from '../types';
import { IChecklistTemplatesApi, IJobChecklistsApi } from '../contracts';
import { HttpApi } from './HttpApi';

export class ChecklistTemplatesHttpApi extends HttpApi implements IChecklistTemplatesApi {
  async listChecklistTemplates(filters: types.ChecklistTemplateListFilters = {}): Promise<types.ChecklistTemplate[]> {
    // Backend returns every matching template in one unpaginated array.
    const config = filters.active === undefined ? undefined : { params: { active: String(filters.active) } };
    const response = await this.http.get<types.APIResponse<types.ChecklistTemplate[]>>('/checklist-templates', config);
    return response.data.data;
  }

  async getChecklistTemplate(id: string): Promise<types.ChecklistTemplate> {
    const response = await this.http.get<types.APIResponse<types.ChecklistTemplate>>(`/checklist-templates/${id}`);
    return response.data.data;
  }

  async createChecklistTemplate(data: types.CreateChecklistTemplateRequest): Promise<types.ChecklistTemplate> {
    const response = await this.http.post<types.APIResponse<types.ChecklistTemplate>>('/checklist-templates', data);
    return response.data.data;
  }

  async replaceChecklistTemplate(id: string, data: types.ReplaceChecklistTemplateRequest): Promise<types.ChecklistTemplate> {
    const response = await this.http.put<types.APIResponse<types.ChecklistTemplate>>(`/checklist-templates/${id}`, data);
    return response.data.data;
  }

  async deleteChecklistTemplate(id: string): Promise<types.ChecklistTemplateDeleteResult> {
    // Unlike most deletes this returns a body: { result: DELETED | DEACTIVATED, template }.
    const response = await this.http.delete<types.APIResponse<types.ChecklistTemplateDeleteResult>>(
      `/checklist-templates/${id}`
    );
    return response.data.data;
  }
}

export class JobChecklistsHttpApi extends HttpApi implements IJobChecklistsApi {
  async getJobChecklist(jobId: string): Promise<types.JobChecklist> {
    const response = await this.http.get<types.APIResponse<types.JobChecklist>>(`/jobs/${jobId}/checklist`);
    return response.data.data;
  }

  async addJobChecklistStep(jobId: string, data: types.AddJobChecklistStepRequest): Promise<types.JobChecklistStep> {
    const response = await this.http.post<types.APIResponse<types.JobChecklistStep>>(`/jobs/${jobId}/checklist/steps`, data);
    return response.data.data;
  }

  async updateJobChecklistStep(
    jobId: string,
    stepId: string,
    data: types.UpdateJobChecklistStepRequest
  ): Promise<types.JobChecklistStep> {
    const response = await this.http.put<types.APIResponse<types.JobChecklistStep>>(
      `/jobs/${jobId}/checklist/steps/${stepId}`,
      data
    );
    return response.data.data;
  }

  async deleteJobChecklistStep(jobId: string, stepId: string): Promise<{ id: string }> {
    const response = await this.http.delete<types.APIResponse<{ id: string }>>(`/jobs/${jobId}/checklist/steps/${stepId}`);
    return response.data.data;
  }

  async completeJobChecklistStep(jobId: string, stepId: string): Promise<types.JobChecklistStep> {
    const response = await this.http.post<types.APIResponse<types.JobChecklistStep>>(
      `/jobs/${jobId}/checklist/steps/${stepId}/complete`
    );
    return response.data.data;
  }

  async uncompleteJobChecklistStep(jobId: string, stepId: string): Promise<types.JobChecklistStep> {
    const response = await this.http.post<types.APIResponse<types.JobChecklistStep>>(
      `/jobs/${jobId}/checklist/steps/${stepId}/uncomplete`
    );
    return response.data.data;
  }

  async applyJobChecklistTemplate(jobId: string, templateId: string): Promise<types.ApplyJobChecklistTemplateResult> {
    const response = await this.http.post<types.APIResponse<types.ApplyJobChecklistTemplateResult>>(
      `/jobs/${jobId}/checklist/apply-template`,
      { templateId }
    );
    return response.data.data;
  }
}
